import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, CalendarBlank } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export function NewsListPage() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Actualités & conseils automobiles — MECA AUTO 17 Tonnay-Charente"
        description="Conseils mécaniques, actualités automobiles, astuces entretien : retrouvez tous les articles de MECA AUTO 17 pour mieux entretenir votre véhicule."
      />
      <section className="pt-32 pb-16 px-6 md:px-12 bg-[#0D2C54] text-white">
        <div className="max-w-[1400px] mx-auto">
          <span className="overline text-[#E63946]">Actualités & conseils</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">Le blog automobile AutoExpert.</h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg">
            Conseils d'entretien, actualités réglementaires, technologies hybrides et électriques : restez informé.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          {articles.length === 0 && (
            <p className="text-center text-[#555555]">Aucun article pour le moment.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {articles.map((a) => (
              <Link
                to={`/actualites/${a.slug}`}
                key={a.id}
                data-testid={`article-${a.slug}`}
                className="bg-white group overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#F8F9FA]">
                  {a.image_url && (
                    <img src={a.image_url} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-[#555555]">
                    <span className="overline text-[#E63946]">{a.category}</span>
                    <span className="flex items-center gap-1"><CalendarBlank size={14} />{new Date(a.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                  <h3 className="mt-4 font-display font-semibold text-lg text-[#0D2C54] leading-snug group-hover:text-[#E63946] transition-colors">{a.title}</h3>
                  <p className="mt-3 text-sm text-[#555555] flex-1">{a.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#E63946] link-arrow">
                    Lire l'article <ArrowRight size={16} weight="bold" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="pt-32 pb-20 px-6 text-center">
        <h1 className="text-3xl font-bold text-[#0D2C54]">Article introuvable</h1>
        <Link to="/actualites"><Button className="mt-6 bg-[#E63946] rounded-none">Retour aux actualités</Button></Link>
      </section>
    );
  }

  if (!article) return <div className="pt-32 px-6 text-[#555555]">Chargement…</div>;

  return (
    <>
      <SEO title={`${article.title} | MECA AUTO 17`} description={article.excerpt} />
      <article data-testid="article-detail" className="pt-32 pb-20 px-6 md:px-12 bg-white">
        <div className="max-w-[900px] mx-auto">
          <Link to="/actualites" className="inline-flex items-center gap-2 text-sm text-[#0D2C54] hover:text-[#E63946] link-arrow">
            <ArrowLeft size={16} weight="bold" /> Retour aux actualités
          </Link>
          <div className="mt-8 flex items-center gap-3 text-xs">
            <span className="overline text-[#E63946]">{article.category}</span>
            <span className="text-[#555555]">{new Date(article.created_at).toLocaleDateString("fr-FR")}</span>
          </div>
          <h1 className="mt-4 text-4xl lg:text-5xl font-bold text-[#0D2C54] tracking-tight">{article.title}</h1>
          <p className="mt-6 text-lg text-[#555555] leading-relaxed">{article.excerpt}</p>
          {article.image_url && (
            <div className="mt-10 aspect-[16/9] overflow-hidden">
              <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="mt-10 prose prose-lg text-[#333333] leading-relaxed whitespace-pre-line">
            {article.content}
          </div>
        </div>
      </article>
    </>
  );
}
